import Swiper from 'swiper';
import { EffectFade } from 'swiper/modules';
import { topics, stages, slideshow } from './content.js';
import { TransitionRenderer } from './effects/transition-renderer.js';
import { SlideshowClock } from './slideshow-clock.js';
import { initCopyMosaic } from './copy-mosaic.js';
import { initStickyCopy } from './sticky-copy.js';
import { initTopicScroll } from './topic-scroll.js';
import vertex from './effects/flyeye.vert?raw';
import fragment from './effects/flyeye.frag?raw';
import polygonFragment from './effects/polygons-fall.frag?raw';
import shardsVertex from './effects/shards-fall.vert?raw';
import shardsFragment from './effects/shards-fall.frag?raw';

export function initSubmv(root, reducedMotion, { desktop, scroller, revealActivity, lenis }) {
  const cleanups = [];
  const listen = (element, type, callback) => {
    element.addEventListener(type, callback);
    cleanups.push(() => element.removeEventListener(type, callback));
  };
  const effectControl = root.querySelector('.effect');
  const effectSelect = effectControl.querySelector('select');
  const renderer = new TransitionRenderer(root.querySelector('canvas'), {
    flyeye: { vertex, fragment },
    'polygons-fall': { vertex, fragment: polygonFragment },
    'shards-fall': { vertex: shardsVertex, fragment: shardsFragment, mesh: true },
  }, slideshow, () => {
    root.classList.remove('is-webgl');
    effectControl.hidden = true;
  });
  const count = root.querySelector('[data-background-count]');
  const copy = root.querySelector('.sub-copy');
  const buttons = [...document.querySelectorAll('[data-topic]')];
  const content = new Map([...topics, ...stages].map((topic) => [topic.id, topic]));
  let manualPause = false;
  let disposed = false;
  let inView = false;
  let loadingRenderer;

  const copyMosaic = initCopyMosaic(copy, (id) => {
    copy.replaceChildren(...content.get(id).paragraphs.map((text) => {
      const paragraph = document.createElement('p');
      paragraph.textContent = text;
      return paragraph;
    }));
  }, topics[0].id, reducedMotion);
  cleanups.push(() => copyMosaic.destroy());
  const stickyCopy = initStickyCopy(root.closest('.submv-hold'), {
    copy, buttons, mosaic: copyMosaic, initialId: topics[0].id, desktop, reducedMotion, scroller,
  });
  cleanups.push(() => stickyCopy.destroy());

  function showTopic(id) {
    if (content.has(id)) stickyCopy.show(id);
  }

  const topicMobile = window.matchMedia('(width < 38rem)');
  buttons.forEach((button) => {
    listen(button, 'pointerenter', (event) => { if (!topicMobile.matches && event.pointerType !== 'touch') showTopic(button.dataset.topic); });
    listen(button, 'focus', () => { if (!topicMobile.matches) showTopic(button.dataset.topic); });
    listen(button, 'click', () => { if (!topicMobile.matches) showTopic(button.dataset.topic); });
  });

  cleanups.push(initTopicScroll(root.closest('.submv-hold'), lenis, {
    mobile: topicMobile, scroller, reducedMotion, select: showTopic,
  }));

  const slider = new Swiper(root.querySelector('.backdrop'), {
    modules: [EffectFade],
    effect: 'fade',
    fadeEffect: { crossFade: true },
    speed: reducedMotion.matches ? 0 : slideshow.duration,
    rewind: true,
    allowTouchMove: false,
    preventInteractionOnTransition: true,
    a11y: false,
    on: {
      slideChange(swiper) {
        const index = swiper.realIndex;
        count.textContent = `${String(index + 1).padStart(2, '0')} / ${String(slideshow.images.length).padStart(2, '0')}`;
        root.dataset.annotated = String(slideshow.images[index].annotated);
        renderer.goTo(index, reducedMotion.matches || revealActivity?.active ? 0 : slideshow.duration);
      },
    },
  });
  root.dataset.annotated = String(slideshow.images[0].annotated);
  const clock = new SlideshowClock(() => slider.slideNext(), slideshow.interval);
  // 初回も、ポリゴンを描画できる準備が整ってから即時開始する。
  clock.pause('renderer');

  async function enableWebgl() {
    if (!inView || document.hidden || reducedMotion.matches || revealActivity?.active || renderer.destroyed || renderer.ready || loadingRenderer) return;
    loadingRenderer = renderer.init(slideshow.images);
    const ready = await loadingRenderer;
    if (disposed) { renderer.destroy(); return; }
    if (ready) {
      renderer.goTo(slider.realIndex, 0);
      root.classList.toggle('is-webgl', !reducedMotion.matches);
      effectControl.hidden = reducedMotion.matches;
    }
    // WebGL不可時も、既存の通常フェードで自動切り替えを再開する。
    clock.resume('renderer');
  }

  function motionChange() {
    slider.params.speed = reducedMotion.matches || revealActivity?.active ? 0 : slideshow.duration;
    root.classList.toggle('is-webgl', renderer.ready && !reducedMotion.matches);
    effectControl.hidden = !renderer.ready || reducedMotion.matches;
    if (reducedMotion.matches) { clock.pause('motion'); renderer.finish(); }
    else { clock.resume('motion'); void enableWebgl(); }
  }

  listen(effectSelect, 'change', () => {
    if (!renderer.setEffect(effectSelect.value)) effectSelect.value = renderer.effect;
    clock.reset();
  });

  root.querySelectorAll('[data-background]').forEach((button) => {
    listen(button, 'click', () => {
      const action = button.dataset.background;
      if (action === 'pause') {
        manualPause = !manualPause;
        if (manualPause) clock.pause('manual'); else clock.resume('manual');
        button.setAttribute('aria-pressed', String(manualPause));
        button.setAttribute('aria-label', `背景の自動切り替えを${manualPause ? '再開' : '停止'}`);
        button.textContent = manualPause ? 'PLAY' : 'PAUSE';
      } else {
        if (!slider.animating) {
          if (action === 'next') slider.slideNext(); else slider.slidePrev();
        }
        clock.reset();
      }
    });
  });

  listen(root, 'focusin', () => clock.pause('focus'));
  listen(root, 'focusout', (event) => { if (!root.contains(event.relatedTarget)) clock.resume('focus'); });
  const cards = document.querySelector('.topics');
  listen(cards, 'focusin', () => clock.pause('card-focus'));
  listen(cards, 'focusout', (event) => { if (!cards.contains(event.relatedTarget)) clock.resume('card-focus'); });
  listen(document, 'visibilitychange', () => {
    if (document.hidden) { clock.pause('hidden'); renderer.finish(); copyMosaic.finish(); }
    else clock.resume('hidden');
  });
  listen(reducedMotion, 'change', motionChange);
  const observer = new IntersectionObserver(([entry]) => {
    inView = entry.isIntersecting;
    if (inView) { clock.resume('offscreen'); if (!reducedMotion.matches) void enableWebgl(); }
    else { clock.pause('offscreen'); renderer.finish(); }
  }, { threshold: .05 });
  observer.observe(root);
  if (document.hidden) clock.pause('hidden');
  motionChange();
  if (revealActivity) cleanups.push(revealActivity.subscribe((active) => {
    copyMosaic.setSuspended(active);
    renderer.setSuspended(active);
    slider.params.speed = active || reducedMotion.matches ? 0 : slideshow.duration;
    if (active) {
      clock.pause('reveal');
      if (slider.animating) slider.transitionEnd(false);
    } else {
      clock.resume('reveal');
      void enableWebgl();
    }
  }));

  return () => {
    disposed = true;
    observer.disconnect();
    clock.destroy();
    renderer.destroy();
    slider.destroy(true, true);
    cleanups.forEach((cleanup) => cleanup());
  };
}
